/**
 * Patient form utility functions
 */
import { PatientFormState, ValidationErrors } from './types';

/**
 * Format date string to MM/DD/YY format for display
 */
export const formatDateForDisplay = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  } catch (e) {
    return dateString;
  }
};

/**
 * Validate patient form data
 * Returns array of validation error messages
 */
export const validatePatientForm = (formData: PatientFormState): ValidationErrors => {
  const errors: ValidationErrors = [];

  if (!formData.firstName?.trim()) {
    errors.push('First name is required');
  }

  if (!formData.lastName?.trim()) {
    errors.push('Last name is required');
  }

  if (!formData.dob) {
    errors.push('Date of birth is required');
  }

  if (!formData.gender) {
    errors.push('Gender is required');
  }

  // Email validation (optional, but if provided must be valid)
  if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.push('Please enter a valid email address');
  }

  // Phone validation
  if (!formData.phone?.trim()) {
    errors.push('Phone number is required');
  } else if (formData.phone.replace(/\D/g, '').length < 10) {
    errors.push('Phone number should have at least 10 digits');
  }

  return errors;
};

/**
 * Parse full name into first, middle, and last name components
 */
export const parseFullName = (
  fullName: string
): { firstName: string; middleName: string; lastName: string } => {
  const nameParts = fullName.split(' ');
  let firstName = '';
  let middleName = '';
  let lastName = '';

  if (nameParts.length === 1) {
    firstName = nameParts[0];
  } else if (nameParts.length === 2) {
    firstName = nameParts[0];
    lastName = nameParts[1];
  } else {
    firstName = nameParts[0];
    lastName = nameParts[nameParts.length - 1];
    middleName = nameParts.slice(1, nameParts.length - 1).join(' ');
  }

  return { firstName, middleName, lastName };
};

/**
 * Format date string to YYYY-MM-DD format for HTML date input
 */
export const formatDateForInput = (dateString: string): string => {
  try {
    const dateObj = new Date(dateString);
    if (!isNaN(dateObj.getTime())) {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  } catch (e) {
    return '';
  }
};
